// Pure core of the item-editor draft ("rozepsaná položka"). Deliberately free of React, zustand and
// every app import so the node harness (draft-core.test.mjs) can compile and exercise THIS file.
// The store wrapper lives in ./draft.ts, the UI in ui/ItemEditor.tsx + ui/DraftRestoreBanner.tsx.
//
// SECURITY (§8, zero-knowledge): a draft holds PLAINTEXT secrets. Nothing here — and nothing in
// ./draft.ts — serialises it: no localStorage, no sessionStorage, no IndexedDB, no network. It lives
// in module memory only, which is exactly what lets it survive the AppShell unmount that an auto-lock
// causes, and what makes a page reload drop it (see ui/useBeforeUnload.ts).

/** Structural stand-in for vault/items' `Field` — kept local so this module has zero imports. */
export interface DraftFieldLike {
  key: string;
  label: string;
  type: string;
  value: string;
  note?: string;
}

/** Structural stand-in for vault/items' `ItemContent`. */
export interface DraftContentLike {
  name: string;
  fields: DraftFieldLike[];
  tags?: string[];
  notes?: string;
}

/** One in-progress item edit. `C` is `ItemContent` in app code; generic keeps this file import-free. */
export interface EditorDraft<C extends DraftContentLike = DraftContentLike> {
  /** Identity of the edit — see `draftKey`. */
  key: string;
  orgId: string;
  /** Set for an edit of an existing item; absent for a new one. */
  itemId?: string;
  folderId: string | null;
  type: string;
  content: C;
  offlineAllowed: boolean;
  /** Revision the edit started from, so a restored edit-draft can still detect a 409. */
  baseRevision?: number;
  wrappedItemKey?: string;
  /** Set when the draft was parked (editor unmounted with unsaved content). */
  parkedAt?: number;
}

/** A parked draft older than this is not offered for restore (it is dropped instead). */
export const PARK_TTL_MS = 30 * 60_000;

/** Identity of an edit: existing items key on the item, new ones on their destination. */
export function draftKey(a: { orgId: string; itemId?: string | null; folderId?: string | null }): string {
  return a.itemId ? `edit:${a.itemId}` : `new:${a.orgId}:${a.folderId ?? "root"}`;
}

/** The comparable part of a draft — what the user can actually change in the editor. */
export function baselineOf(d: Pick<EditorDraft, "type" | "content" | "offlineAllowed">): string {
  return JSON.stringify({ type: d.type, content: d.content, offlineAllowed: d.offlineAllowed });
}

/** True when the draft differs from the baseline captured when the editor opened. */
export function isDirty(d: Pick<EditorDraft, "type" | "content" | "offlineAllowed">, baseline: string): boolean {
  return baselineOf(d) !== baseline;
}

/** Nothing worth keeping: no name and no field value (guards against parking an untouched form). */
export function isEmptyDraft(d: Pick<EditorDraft, "content">): boolean {
  const c = d.content;
  if (c.name.trim()) return false;
  if ((c.notes ?? "").trim()) return false;
  if ((c.tags ?? []).length > 0) return false;
  return c.fields.every((f) => !f.value.trim());
}

export function isExpired(d: Pick<EditorDraft, "parkedAt">, now: number): boolean {
  return d.parkedAt != null && now - d.parkedAt > PARK_TTL_MS;
}

/** Should `parked` be offered to an editor opening on `want`? Mode and target must line up. */
export function shouldRestore(
  parked: EditorDraft | null | undefined,
  want: { orgId: string; itemId?: string | null },
  now: number,
): boolean {
  if (!parked || isExpired(parked, now)) return false;
  if (want.itemId) return parked.itemId === want.itemId;
  // A new-item draft is offered anywhere in the vault it was started in; the caller restores its
  // folder first, so the destination still ends up being the one the user picked.
  return !parked.itemId && parked.orgId === want.orgId;
}

/** Label for the restore banner. */
export function draftTitle(d: EditorDraft): string {
  return d.content.name.trim();
}

/** Best-effort scrub before dropping a draft: overwrite the strings we can still reach. */
export function wipeDraft(d: EditorDraft | null | undefined): void {
  if (!d) return;
  for (const f of d.content.fields) {
    f.value = "";
    if (f.note) f.note = "";
  }
  d.content.name = "";
  d.content.notes = "";
  d.content.tags = [];
}
